// ============================================================================
// WORKSHOP SERVICE - frontend/src/services/workshop.ts
// API calls for workshop functionality
// ============================================================================

import { api } from './api';
import { AvatarRecommendedAction } from '../types/avatarActions';

// A single cognitive-policy graph element (lens/question/evidenceRule/decisionCriterion)
// that activated for a given turn, with human-readable text.
export interface ActivatedCognitiveElement {
  id: string;
  type: 'lens' | 'question' | 'evidenceRule' | 'decisionCriterion';
  text: string;
}

// A graph node (Contribution, Persona, UserStory, etc.) an avatar cited as evidence.
// The avatar supplies id/type/label directly — no separate lookup needed.
export interface LinkedNodeRef {
  id: string;
  type: string;
  label: string;
}

export interface AvatarSuggestion {
  role: string;
  name: string;
  response: string;
  preview: string;
  urgency: 'urgent' | 'important' | 'normal';
  audioBase64?: string;
  audioUrl?: string;
  audioS3Key?: string; // S3 key for persisted TTS audio — survives across sessions
  commandRowId?: string; // speech_commands row id — scopes release-speech to this one row
  audioGenerating?: boolean; // True while async TTS is in progress
  governorReason: string;
  governorPriority: number;
  state?: 'pending' | 'accepted' | 'ignored' | 'rejected'; // Persisted state
  contributionId?: string; // Unique ID for graph feedback
  avatarDefinitionId?: string; // Avatar definition ID for learning
  createdAt?: string; // ISO timestamp when contribution was created
  // Absent means avatar-generated, which keeps every pre-existing row valid.
  // 'manual' marks one a facilitator wrote by hand.
  source?: 'manual';
  // Cognitive policy trace — only present when the avatar has a cognitivePolicy
  // and the workflow's cognitive path activated one.
  factsConsidered?: string[];
  assumptions?: string[];
  evidenceUsed?: string[];
  questionsRaised?: string[];
  confidence?: number;
  activatedElements?: ActivatedCognitiveElement[];
  reasoningDepth?: 'single' | 'multi-step';
  recommendedActions?: AvatarRecommendedAction[];
  linkedNodes?: LinkedNodeRef[];
  // Set when this contribution came from a user-story avatar-review request
  // rather than an ordinary live-session trigger — lets the UI label and group
  // review contributions distinctly.
  reviewScope?: { type: 'story' | 'selection' | 'persona'; persona?: string; storyIds: string[] };
}

type ConversationEngagementLevel = 'high' | 'medium' | 'low';

export interface FacilitatorGuidance {
  timestamp: string;
  phase: string;
  summary: string;
  missingPerspectives: Array<{
    avatar: string;
    reason: string;
    importance: 'critical' | 'important' | 'nice-to-have';
  }>;
  conversationHealth: {
    balanceScore: number;
    engagementLevel: ConversationEngagementLevel;
    concerns: string[];
  };
  suggestedPrompts: string[];
  nextStepRecommendation: string;
}

export interface PersonaBackstory {
  age?: string;
  gender?: string;
  education?: string;
  familySituation?: string;
  background?: string;
  traits: string[];
}

export interface ExtractedPersona {
  id: string;
  name: string;
  role: string;
  description: string;
  goals: string[];
  painPoints: string[];
  backstory?: PersonaBackstory;
  // Accumulative tracking metadata
  firstSeen?: string;
  lastSeen?: string;
  isNew?: boolean;
  originalId?: string; // Track the original LLM-generated ID
}

export interface UserStory {
  id: string;
  persona: string;
  story: string;
  acceptanceCriteria: string[];
  priority: 'high' | 'medium' | 'low';
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  rank?: number;
  tags?: string[];
  featureIds?: string[];
  notes?: string[];        // free-text working notes, distinct from acceptanceCriteria
  source?: string;         // provenance
  milestoneId?: string;    // delivery phase this story ships in
  // Accumulative tracking metadata
  firstSeen?: string;
  lastSeen?: string;
  isNew?: boolean;
  originalId?: string; // Track the original LLM-generated ID
  // Locking: once true, no user or AI write path can edit/delete this story until unlocked
  locked?: boolean;
  lockedBy?: string;
  lockedAt?: string;
  lockReason?: string;
  // Avatar-suggested grouping of related stories
  groupId?: string;
  groupLabel?: string;
}

export interface Feature {
  id: string;
  sessionId: string;
  name: string;
  description: string;
  relatedPersonas?: string[];
  status?: 'draft' | 'confirmed';
  notes?: string[];
  source?: string;
  milestoneId?: string;
  createdAt?: string;
}

export interface TechnicalSpecs {
  coreFunctionality: string[];
  integrationPoints: string[];
  dataRequirements: string[];
  securityConsiderations: string[];
  performanceTargets: string[];
}

export interface Constraints {
  technical: string[];
  business: string[];
  regulatory: string[];
}

export interface NFR {
  id: string;
  category: 'performance' | 'security' | 'scalability' | 'availability' | 'usability' | 'maintainability' | 'compliance';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  acceptanceCriteria: string[];
  featureIds?: string[];
  notes?: string[];
  source?: string;
  // Stored so exports stay coherent, but deliberately not surfaced in the
  // milestone UI: most NFRs are cross-cutting.
  milestoneId?: string;
}

export interface ADR {
  id: string;
  sessionId: string;
  title: string;
  status: 'proposed' | 'accepted' | 'deprecated' | 'superseded';
  context: string;
  decision: string;
  consequences: string;
  alternatives?: string[];
  featureIds?: string[];
  notes?: string[];
  source?: string;
  createdAt?: string;
}

export interface ProductRequirements {
  projectName: string;
  generatedAt: string;
  sessionId: string;
  personas: ExtractedPersona[];
  userStories: UserStory[];
  technicalSpecs: TechnicalSpecs;
  constraints: Constraints;
  assumptions: string[];
  nonFunctionalRequirements?: NFR[];
}

export interface RequirementsExtractionResponse {
  success: boolean;
  sessionId: string;
  requirements: ProductRequirements;
  timestamp: string;
}

/**
 * Generate TTS audio for avatar contribution (called on-demand when user accepts)
 */
export async function generateTTS(
  text: string,
  avatarRole: string
): Promise<{ success: boolean; audioBase64: string; audioUrl: string; format: string; durationMs: number }> {
  const response = await api.post('/api/sessions/workshop/generate-tts', {
    text,
    avatarRole
  });

  return response.data;
}

export async function approveAvatarContribution(
  sessionId: string,
  avatarRole: string,
  text: string,
  audioBase64: string
): Promise<{ success: boolean; playbackMode: string; message: string; audioBase64?: string }> {
  const response = await api.post('/api/sessions/workshop/approve-avatar', {
    sessionId,
    avatarRole,
    text,
    audioBase64
  });

  return response.data;
}

/**
 * Get facilitator guidance for current phase
 */
export async function getFacilitatorGuidance(
  sessionId: string,
  phase: string,
  phaseObjective: string,
  phaseGoal?: string,
  successCriteria?: string[]
): Promise<FacilitatorGuidance> {
  const response = await api.post('/api/sessions/workshop/guidance', {
    sessionId,
    phase,
    phaseObjective,
    phaseGoal,
    successCriteria
  });

  return response.data;
}

/**
 * Extract current requirements from session (non-destructive, can be called periodically)
 */
export async function extractRequirements(
  sessionId: string,
  transcript: any[],
  customerName?: string,
  personas?: any[],
  guidance?: any[]
): Promise<RequirementsExtractionResponse> {
  const response = await api.post(`/api/sessions/${sessionId}/extract-requirements`, {
    transcript,
    customerName,
    personas,
    guidance
  });

  return response.data;
}

/**
 * Get stored requirements for a session
 * Returns null if no requirements have been extracted yet
 */
export async function getStoredRequirements(
  sessionId: string
): Promise<ProductRequirements | null> {
  try {
    const response = await api.get(`/api/sessions/${sessionId}/requirements`);
    return response.data.requirements;
  } catch (error: any) {
    // 404 means no requirements stored yet
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Failed to get stored requirements:', error);
    return null;
  }
}

export async function clearRequirements(sessionId: string): Promise<void> {
  await api.delete(`/api/sessions/${sessionId}/requirements`);
}

export async function extractNFRs(
  sessionId: string,
  customerName?: string
): Promise<{ success: boolean; sessionId: string; task: any }> {
  const response = await api.post(`/api/sessions/${sessionId}/extract-nfrs`, {
    customerName
  });
  return response.data;
}

/**
 * Get all features for a session
 */
export async function getFeatures(sessionId: string): Promise<Feature[]> {
  const response = await api.get(`/api/sessions/${sessionId}/features`);
  return response.data.features || [];
}

/**
 * Create a new feature
 */
export async function createFeature(
  sessionId: string,
  patch: Partial<Feature>
): Promise<Feature> {
  const response = await api.post(`/api/sessions/${sessionId}/features`, patch);
  return response.data.feature;
}

/**
 * Update an existing feature
 */
export async function updateFeature(
  sessionId: string,
  featureId: string,
  patch: Partial<Feature>
): Promise<void> {
  await api.patch(`/api/sessions/${sessionId}/features/${featureId}`, patch);
}

/**
 * Delete a feature
 */
export async function deleteFeature(sessionId: string, featureId: string): Promise<void> {
  await api.delete(`/api/sessions/${sessionId}/features/${featureId}`);
}

export async function extractADRs(
  sessionId: string,
  customerName?: string
): Promise<{ success: boolean; sessionId: string; task: any }> {
  const response = await api.post(`/api/sessions/${sessionId}/extract-adrs`, {
    customerName
  });
  return response.data;
}

/**
 * Get all ADRs for a session
 */
export async function getADRs(sessionId: string): Promise<ADR[]> {
  const response = await api.get(`/api/sessions/${sessionId}/adrs`);
  return response.data.adrs || [];
}

/**
 * Create a new ADR
 */
export async function createADR(
  sessionId: string,
  patch: Partial<ADR>
): Promise<ADR> {
  const response = await api.post(`/api/sessions/${sessionId}/adrs`, patch);
  return response.data.adr;
}

/**
 * Update an existing ADR
 */
export async function updateADR(
  sessionId: string,
  adrId: string,
  patch: Partial<ADR>
): Promise<void> {
  await api.patch(`/api/sessions/${sessionId}/adrs/${adrId}`, patch);
}

/**
 * Delete an ADR
 */
export async function deleteADR(sessionId: string, adrId: string): Promise<void> {
  await api.delete(`/api/sessions/${sessionId}/adrs/${adrId}`);
}

// Win theme — story #177. Deliberately minimal (title/description only, no
// rich editing) — a new core entity, since no win-theme capture existed
// anywhere in a standard session before this story.
export interface WinTheme {
  id: string;
  sessionId: string;
  title: string;
  description: string;
  createdAt: string;
  createdBy?: string;
}

export async function getWinThemes(sessionId: string): Promise<WinTheme[]> {
  const response = await api.get(`/api/sessions/${sessionId}/win-themes`);
  return response.data.winThemes || [];
}

export async function createWinTheme(sessionId: string, patch: { title: string; description?: string }): Promise<WinTheme> {
  const response = await api.post(`/api/sessions/${sessionId}/win-themes`, patch);
  return response.data.winTheme;
}

export async function updateWinTheme(
  sessionId: string,
  winThemeId: string,
  patch: { title?: string; description?: string }
): Promise<WinTheme> {
  const response = await api.patch(`/api/sessions/${sessionId}/win-themes/${winThemeId}`, patch);
  return response.data.winTheme;
}

export async function deleteWinTheme(sessionId: string, winThemeId: string): Promise<void> {
  await api.delete(`/api/sessions/${sessionId}/win-themes/${winThemeId}`);
}

export async function extractWinThemes(
  sessionId: string,
  customerName?: string
): Promise<{ success: boolean; sessionId: string; task: any }> {
  const response = await api.post(`/api/sessions/${sessionId}/extract-win-themes`, {
    customerName
  });
  return response.data;
}
